import React from "react";
import { logout } from "../../store/auth.tsx";
import { fetchExamples } from "../../store/example.tsx";
import { BASE_URL } from "../../const.ts";
import {
  conversationActions,
  fetchFavouriteQuestions,
  fetchMemories,
  fetchSynchronizationStatus,
  fetchSpreadsheets,
} from "../../store/conversation.tsx";
import { streamAsk } from "./streamAsk.tsx";
import handleSignUp from "./signup/handleSignUp.tsx";
import { AnonymousSession } from "../../store/types.tsx";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";

interface HandleKeyPressProps {
  searchText: string;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  setSearchInputError: React.Dispatch<React.SetStateAction<boolean>>;
  onPulseAnimation?: () => void;
  stopAnimation: () => void;
  dispatch: any;
  selectedConversation: string;
  selectedModel: string;
  stopStreamRef: React.RefObject<(() => void) | null>;
}

const getAuthenticatedKeyPressHandler = ({
  searchText,
  setSearchText,
  setSearchInputError,
  onPulseAnimation,
  stopAnimation,
  dispatch,
  selectedConversation,
  selectedModel,
  stopStreamRef,
}: HandleKeyPressProps) => {
  return async (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();

      if (onPulseAnimation) onPulseAnimation();

      if (searchText.trim() === "") {
        setSearchInputError(true);
        stopAnimation();
        return;
      } else if (
        searchText.trim().toLowerCase() ===
        i18n
          ._(msg({ message: "Logout", context: "Action to logout the user" }))
          .toLowerCase()
      ) {
        stopAnimation();
        dispatch(logout());
        dispatch({ type: "RESET_STATE" });
        dispatch(fetchExamples({ isLoggedIn: false }) as any);
        return;
      } else {
        setSearchInputError(false);
        setSearchText("");
      }

      if (
        searchText.trim().toLowerCase() ===
        i18n
          ._(
            msg({
              message: "Show favourite questions",
              context: "Action to show the favorite questions of the user",
            }),
          )
          .toLowerCase()
      ) {
        dispatch(fetchFavouriteQuestions() as any);
        dispatch(
          conversationActions.appendExchange({
            prompt: i18n._(
              msg({
                message: "Show favourite questions",
                context: "Action to show the favorite questions of the user",
              }),
            ),
            response: null,
          }),
        );
        stopAnimation();
        return;
      }

      if (
        searchText.trim().toLowerCase() ===
        i18n
          ._(
            msg({
              message: "Show memories",
              context: "Action to show the create memories of the user",
            }),
          )
          .toLowerCase()
      ) {
        dispatch(fetchMemories() as any);
        dispatch(
          conversationActions.appendExchange({
            prompt: i18n._(
              msg({
                message: "Show memories",
                context: "Action to show the create memories of the user",
              }),
            ),
            response: null,
          }),
        );
        stopAnimation();
        return;
      }

      if (
        searchText.trim().toLowerCase() ===
        i18n
          ._(
            msg({
              message: "Show Spreadsheets",
              context: "Action to show the spreadsheets for the tenants",
            }),
          )
          .toLowerCase()
      ) {
        dispatch(fetchSpreadsheets() as any);
        dispatch(
          conversationActions.appendExchange({
            prompt: i18n._(
              msg({
                message: "Show Spreadsheets",
                context: "Action to show the spreadsheets for the tenants",
              }),
            ),
            response: null,
          }),
        );
        stopAnimation();
        return;
      }

      if (
        searchText.trim().toLowerCase() ===
        i18n
          ._(
            msg({
              message: "Integration status",
              context:
                "Action to show the integration status of the application",
            }),
          )
          .toLowerCase()
      ) {
        const integrationStatusPrompt = i18n._(
          msg({
            message: "Integration status",
            context: "Action to show the integration status of the application",
          }),
        );
        const temporaryConversationId =
          selectedConversation && selectedConversation !== ""
            ? selectedConversation
            : "new";
        const temporaryExchangeId = `sync-status-temp-${Date.now()}`;

        dispatch(
          conversationActions.appendExchange({
            id: temporaryExchangeId,
            conversation_id: temporaryConversationId,
            prompt: integrationStatusPrompt,
            response: null,
          }),
        );
        dispatch(
          fetchSynchronizationStatus(
            integrationStatusPrompt,
            temporaryExchangeId,
            temporaryConversationId,
          ) as any,
        );
        stopAnimation();
        return;
      }

      let exchangeParams = {
        conversation_id: "",
        prompt: searchText,
        model: selectedModel,
      };

      if (selectedConversation !== "" && selectedConversation !== "new") {
        exchangeParams.conversation_id = selectedConversation;
      }
      await streamAsk({
        dispatch,
        isAuthenticated: true,
        stopAnimation: stopAnimation,
        exchangeURL: "/v1/exchange/",
        streamSlug: "/v1/stream_ask/",
        exchangeParams: exchangeParams,
        stopStreamRef,
      });
    }
  };
};

interface UnauthenticatedHandleKeyPressProps {
  searchText: string;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  setSearchInputError: React.Dispatch<React.SetStateAction<boolean>>;
  onPulseAnimation?: () => void;
  stopAnimation: () => void;
  currentConversation: string;
  dispatch: any;
  anonymousSession: AnonymousSession | null;
  stopStreamRef: React.RefObject<(() => void) | null>;
}

const getUnauthenticatedKeyPressHandler = ({
  searchText,
  setSearchText,
  setSearchInputError,
  onPulseAnimation,
  stopAnimation,
  currentConversation,
  dispatch,
  anonymousSession,
  stopStreamRef,
}: UnauthenticatedHandleKeyPressProps) => {
  return async (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      console.log(`Anonymous session: ${anonymousSession}`);

      if (onPulseAnimation) onPulseAnimation();
      if (searchText.trim() === "") {
        setSearchInputError(true);
        stopAnimation();
        return;
      } else if (
        searchText.trim().toLowerCase() ===
        i18n
          ._(
            msg({
              message: "Login with google",
              context: "Message initiating the login flow with Google",
            }),
          )
          .toLowerCase()
      ) {
        window.location.href = BASE_URL + "/auth/google-login";
        return;
      } else if (
        searchText.trim().toLowerCase() ===
        i18n
          ._(
            msg({
              message: "Login with e-conomic",
              context: "Message initiating the login flow with e-conomic",
            }),
          )
          .toLowerCase()
      ) {
        window.location.href = BASE_URL + "/v1/auth/visma-connect-login";
        return;
      } else if (
        searchText.trim().toLowerCase() ===
        i18n
          ._(
            msg({
              message: "Login with Microsoft",
              context: "Message initiating the login flow with Microsoft",
            }),
          )
          .toLowerCase()
      ) {
        window.location.href = BASE_URL + "/v1/auth/microsoft-login";
        return;
      } else if (
        searchText.trim().toLowerCase() ===
          i18n
            ._(
              msg({
                message: "try for free",
                context: "Message initiating the signup flow",
              }),
            )
            .toLowerCase() ||
        (anonymousSession &&
          anonymousSession.signUpState &&
          !anonymousSession.signUpState.isComplete)
      ) {
        setSearchInputError(false);
        dispatch(
          conversationActions.appendCustomActionPrompt({
            text: searchText,
            type: "TextResponse",
          }),
        );
        await handleSignUp({
          searchText,
          dispatch,
          signUpState: anonymousSession?.signUpState,
        });
        stopAnimation();
        setSearchText("");

        return;
      } else {
        setSearchInputError(false);
        setSearchText("");
      }
      let exchangeParams = {
        conversation_id: "",
        prompt: searchText,
      };

      if (currentConversation !== "" && currentConversation !== "new") {
        exchangeParams.conversation_id = currentConversation;
      }
      await streamAsk({
        dispatch,
        isAuthenticated: false,
        stopAnimation: stopAnimation,
        exchangeURL: "/v1/anonymous/exchange/",
        streamSlug: "/v1/anonymous/stream_ask/",
        exchangeParams: exchangeParams,
        stopStreamRef,
      });
    }
  };
};

export { getAuthenticatedKeyPressHandler, getUnauthenticatedKeyPressHandler };
